import { useEffect, useState } from 'react';
import { useSession } from '@/app/session';
import { publicEventsApi } from '@/features/publicsite/events/api/publicEventsApi';
import type { EventDetail } from '@/features/publicsite/types/events/EventDetail';
import type { EventParticipation } from '@/features/publicsite/types/events/EventParticipation';

/**
 * The signed-in visitor's own standing with an event — registered, present, allowed to withdraw or
 * to evaluate it, or already has. Null for an anonymous visitor. `setParticipation` lets the page
 * record an evaluation it has just had accepted without asking again; `refresh` asks again after a
 * registration or a withdrawal, whose effect on what may be offered next is the server's to say.
 */
export function useEventParticipation(event: EventDetail | null) {
  const { user } = useSession();
  const [participation, setParticipation] = useState<EventParticipation | null>(null);
  const [asked, setAsked] = useState(0);

  // Only a signed-in visitor can have registered, so there is nothing to ask for anyone else —
  // and this is the one call on the page that needs a session, not just an event id.
  useEffect(() => {
    if (!event || !user) {
      setParticipation(null);
      return;
    }
    let live = true;
    publicEventsApi
      .participation(event.id)
      .then((p) => {
        if (live) setParticipation(p);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [event, user, asked]);

  return { participation, setParticipation, refresh: () => setAsked((n) => n + 1) };
}
